import { useTranslation } from "react-i18next";

import StatusPill, { StatusPillColour } from "../shared/StatusPill";
import Tooltip from "../shared/Tooltip";
import classNames from "../../utils/classNames";

/** Mirrors Vehicle.VatSalesTreatment on the API. */
export enum VatSalesTreatment {
  Unset = 0,
  Standard = 1,
  MarginScheme = 2,
}

type VehicleVatStatusCellProps = {
  /** Vehicle.ApplyVAT — the source of truth, not the lead mirror. */
  applyVAT?: boolean | null;
  /** Stamped sales treatment; the pill is hidden while unset (scheme off, or not yet collected). */
  vatSalesTreatment?: number | null;
  classes?: string;
};

/**
 * Purchase VAT + stamped sales treatment pills for table rows.
 * ApplyVAT means "the seller charged VAT on the ELV purchase", not "this vehicle is in the
 * margin scheme" — the tooltips spell that out because staff read it the other way round.
 */
export default function VehicleVatStatusCell({
  applyVAT,
  vatSalesTreatment,
  classes,
}: VehicleVatStatusCellProps) {
  const { t } = useTranslation("vehicles");

  const purchaseVat = applyVAT ?? false;
  const showTreatment =
    vatSalesTreatment != null && vatSalesTreatment !== VatSalesTreatment.Unset;
  const isMargin = vatSalesTreatment === VatSalesTreatment.MarginScheme;

  return (
    // nowrap so "NO VAT" stays on one line in narrow table columns.
    <div className={classNames("flex flex-col items-start gap-1 whitespace-nowrap", classes)}>
      <Tooltip
        content={
          purchaseVat
            ? t("VAT charged on purchase (trade seller)")
            : t("No VAT on purchase (private seller)")
        }
      >
        <StatusPill
          text={purchaseVat ? t("VAT") : t("NO VAT")}
          colour={purchaseVat ? StatusPillColour.Green : StatusPillColour.Neutral}
        />
      </Tooltip>

      {showTreatment && (
        <Tooltip
          content={
            isMargin
              ? t("Parts sell under the margin scheme (no separate VAT)")
              : t("Parts sell standard-rated")
          }
        >
          <StatusPill
            text={isMargin ? t("MARGIN") : t("STANDARD")}
            colour={isMargin ? StatusPillColour.Orange : StatusPillColour.Blue}
          />
        </Tooltip>
      )}
    </div>
  );
}
